import { Suspense, type ReactNode } from 'react';
import { Link, isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function PageSpinner() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
    </div>
  );
}

/** Wraps lazily loaded pages so the layout stays on screen while the chunk loads. */
export function LazyBoundary({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageSpinner />}>{children}</Suspense>;
}

function FallbackCard({ title, message, children }: { title: string; message: string; children?: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-center">
      <AlertCircle className="h-10 w-10 text-destructive" />
      <h1 className="text-2xl font-semibold">{title}</h1>
      <p className="max-w-md text-muted-foreground">{message}</p>
      <div className="flex gap-2">{children}</div>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <FallbackCard title="Page not found" message="This page does not exist or was moved.">
      <Button asChild>
        <Link to="/dashboard">Go to dashboard</Link>
      </Button>
    </FallbackCard>
  );
}

/** Route-level errorElement: render errors and failed lazy chunk loads land here instead of a blank screen. */
export function RouteErrorPage() {
  const error = useRouteError();
  console.error('Route error:', error);

  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;

  const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
  return (
    <FallbackCard title="Something went wrong" message={message}>
      <Button onClick={() => window.location.reload()}>Reload page</Button>
      <Button variant="outline" asChild>
        <Link to="/dashboard">Go to dashboard</Link>
      </Button>
    </FallbackCard>
  );
}
